import { MeterOwnerType } from '@enmon/commons/lib/enmon/meters';
import { Commodity } from '@enmon/commons/lib/shared';
import {
  DiameterNominal,
  ElectricityDeviceCategory,
  ElectricityMeasurementCategory,
  ElectricityMeterMeasurementType,
  FLOWRATE_Q3_VALUES,
  FLOWRATE_QN_VALUES,
  FLOWRATE_QP_VALUES,
  FlowrateUnit,
  GasDeviceCategory,
  GasMeasurementCategory,
  GSize,
  getGasLayLengthValues,
  getLayLengthValues,
  InstallationPosition,
  isGasDeviceCategory,
  MAX_CURRENT_VALUES,
  MeasuringCurrentTransformer,
  MIN_CURRENT_VALUES,
  Mounting,
  Orientation,
  OtherDeviceCategory,
  P_MAX_VALUES,
  PHASES,
  REF_CURRENT_VALUES,
  Resistance,
  SinglePhaseCircuitBreakerSize,
  ThermalMeasurementType,
  TriplePhaseCircuitBreakerSize,
  TubeMaterial,
  WaterMeterMeasurementType,
  DIAMETER_NOMINAL_LABELS,
  formatPmax,
} from '@enmon/commons/lib/shared/meter';
import type { InventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import type { InventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';
import { getTariffOptions } from '@/shared/meters/register-metadata';

export function getDetailEnumOptions(
  key: string,
  commodity: InventorizationIdentity['commodity'],
  detail: InventorizationTechnicalDetails['detail'],
) {
  switch (key) {
    case 'circuitBreakerSize':
      return getCircuitBreakerSizeOptions(detail.phases);
    case 'deviceCategory':
      return getDeviceCategoryOptions(commodity);
    case 'diameterNominal':
      return Object.values(DiameterNominal).map(value => ({ label: DIAMETER_NOMINAL_LABELS[value], value }));
    case 'flowrate':
      return flowrateOptions();
    case 'gSize':
      return enumValueOptions(GSize);
    case 'thermalMeasurementType':
      return enumValueOptions(ThermalMeasurementType);
    case 'iMax':
      return numberValueOptionsWithUnit(MAX_CURRENT_VALUES, 'A');
    case 'iMin':
      return numberValueOptionsWithUnit(MIN_CURRENT_VALUES, 'A');
    case 'iRef':
      return numberValueOptionsWithUnit(REF_CURRENT_VALUES, 'A');
    case 'installationPosition':
      return translatedEnumValueOptions(InstallationPosition, 'app.common.installationPosition');
    case 'layLength':
      return layLengthOptions(commodity, detail.deviceCategory);
    case 'measurementCategory':
      return getMeasurementCategoryOptions(commodity);
    case 'measurementType':
      return getMeasurementTypeOptions(commodity);
    case 'measuringCurrentTransformer':
      return enumValueOptions(MeasuringCurrentTransformer);
    case 'mounting':
      return translatedEnumValueOptions(Mounting, 'app.common.mounting');
    case 'orientation':
      return translatedEnumValueOptions(Orientation, 'app.common.orientation');
    case 'pMax':
      return P_MAX_VALUES.map(value => ({ label: formatPmax(value), value: String(value) }));
    case 'ownerType':
      return ownerTypeOptions();
    case 'phases':
      return PHASES.map(phase => ({ label: `${phase} phase${phase === 1 ? '' : 's'}`, value: String(phase) }));
    case 'resistance':
      return enumValueOptions(Resistance);
    case 'tariffs':
      return getTariffOptions();
    case 'tubeMaterial':
      return translatedEnumValueOptions(TubeMaterial, 'app.common.tubeMaterial');
    default:
      return null;
  }
}

export function formatDetailLabel(key: string) {
  return key
    .replaceAll('_', ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();
}

export function formatDetailDisplayValue(key: string, value: string, commodity: InventorizationIdentity['commodity']) {
  if (!value) return undefined;

  switch (key) {
    case 'flange':
    case 'hotWater':
    case 'woltman':
      return formatBooleanDisplayValue(value);
    case 'deviceCategory':
      return formatDeviceCategoryDisplayValue(commodity, value);
    case 'diameterNominal':
      return DIAMETER_NOMINAL_LABELS[value as DiameterNominal];
    case 'installationPosition':
      return translateEnumValue('app.common.installationPosition', value);
    case 'measurementType':
      return formatMeasurementTypeDisplayValue(commodity, value);
    case 'mounting':
      return translateEnumValue('app.common.mounting', value);
    case 'orientation':
      return translateEnumValue('app.common.orientation', value);
    case 'pMax':
      return P_MAX_VALUES.includes(Number(value)) ? formatPmax(Number(value)) : undefined;
    case 'tubeMaterial':
      return translateEnumValue('app.common.tubeMaterial', value);
    default:
      return undefined;
  }
}

function getCircuitBreakerSizeOptions(phases: string | undefined) {
  if (phases === '1') {
    return enumValueOptions(SinglePhaseCircuitBreakerSize);
  }

  if (phases === '3') {
    return enumValueOptions(TriplePhaseCircuitBreakerSize);
  }

  return enumValueOptions({ ...SinglePhaseCircuitBreakerSize, ...TriplePhaseCircuitBreakerSize });
}

function getDeviceCategoryOptions(commodity: InventorizationIdentity['commodity']) {
  switch (commodity) {
    case Commodity.Electricity:
      return translatedEnumValueOptions(ElectricityDeviceCategory, 'app.common.electricityDeviceCategory');
    case Commodity.NaturalGas:
      return translatedEnumValueOptions(GasDeviceCategory, 'app.common.gasDeviceCategory');
    case Commodity.Other:
      return translatedEnumValueOptions(OtherDeviceCategory, 'app.common.otherDeviceCategory');
    default:
      return null;
  }
}

function getMeasurementCategoryOptions(commodity: InventorizationIdentity['commodity']) {
  switch (commodity) {
    case Commodity.Electricity:
      return enumValueOptions(ElectricityMeasurementCategory);
    case Commodity.NaturalGas:
      return enumValueOptions(GasMeasurementCategory);
    default:
      return null;
  }
}

function getMeasurementTypeOptions(commodity: InventorizationIdentity['commodity']) {
  switch (commodity) {
    case Commodity.Electricity:
      return translatedEnumValueOptions(ElectricityMeterMeasurementType, 'app.common.measurementType.electricity');
    case Commodity.Water:
      return translatedEnumValueOptions(WaterMeterMeasurementType, 'app.common.measurementType.water');
    default:
      return null;
  }
}

function enumValueOptions(values: Record<string, string>) {
  return Object.values(values).map(value => ({
    label: formatEnumLabel(value),
    value,
  }));
}

function translatedEnumValueOptions(values: Record<string, string>, translationBase: string) {
  return Object.values(values).map(value => ({
    label: translateEnumValue(translationBase, value),
    value,
  }));
}

function ownerTypeOptions() {
  return Object.values(MeterOwnerType).map(value => ({
    label: translateInventoryText(`app.common.meterOwnerType.${value}`, formatEnumLabel(value)),
    value,
  }));
}

function numberValueOptionsWithUnit(values: readonly number[], unit: string) {
  return values.map(value => ({
    label: `${value} ${unit}`,
    value: String(value),
  }));
}

function layLengthOptions(commodity: InventorizationIdentity['commodity'], deviceCategory: string | undefined) {
  switch (commodity) {
    case Commodity.NaturalGas:
      return numberValueOptionsWithUnit(getGasLayLengthOptionValues(deviceCategory), 'mm');
    case Commodity.Thermal:
      return numberValueOptionsWithUnit(getLayLengthValues(Commodity.Thermal), 'mm');
    case Commodity.Water:
      return numberValueOptionsWithUnit(getLayLengthValues(Commodity.Water), 'mm');
    default:
      return null;
  }
}

function getGasLayLengthOptionValues(deviceCategory: string | undefined) {
  const values = isGasDeviceCategory(deviceCategory)
    ? getGasLayLengthValues(deviceCategory)
    : Object.values(GasDeviceCategory).flatMap(category => getGasLayLengthValues(category));

  return [...new Set(values)].toSorted((left, right) => left - right);
}

function flowrateOptions() {
  return [
    ...FLOWRATE_Q3_VALUES.map(value => [FlowrateUnit.Q3, value] as const),
    ...FLOWRATE_QN_VALUES.map(value => [FlowrateUnit.Qn, value] as const),
    ...FLOWRATE_QP_VALUES.map(value => [FlowrateUnit.Qp, value] as const),
  ].map(([unit, value]) => ({
    label: `${unit} ${value}`,
    value: `${unit} ${value}`,
  }));
}

function formatBooleanDisplayValue(value: string) {
  switch (value) {
    case 'true':
      return translateInventoryText('app.inventory.review.values.yes', 'Yes');
    case 'false':
      return translateInventoryText('app.inventory.review.values.no', 'No');
    default:
      return undefined;
  }
}

function formatMeasurementTypeDisplayValue(commodity: InventorizationIdentity['commodity'], value: string) {
  switch (commodity) {
    case Commodity.Electricity:
      return translateEnumValue('app.common.measurementType.electricity', value);
    case Commodity.Water:
      return translateEnumValue('app.common.measurementType.water', value);
    default:
      return undefined;
  }
}

function formatDeviceCategoryDisplayValue(commodity: InventorizationIdentity['commodity'], value: string) {
  switch (commodity) {
    case Commodity.Electricity:
      return translateEnumValue('app.common.electricityDeviceCategory', value);
    case Commodity.NaturalGas:
      return translateEnumValue('app.common.gasDeviceCategory', value);
    case Commodity.Other:
      return translateEnumValue('app.common.otherDeviceCategory', value);
    default:
      return undefined;
  }
}

function translateEnumValue(translationBase: string, value: string) {
  return translateInventoryText(`${translationBase}.${value}`, formatEnumLabel(value));
}

function formatEnumLabel(value: string) {
  return value
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/^./, match => match.toUpperCase());
}
